import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRouteSnapshot, NavigationEnd, Router, TitleStrategy } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../environments/environment';

type GtagWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };

/**
 * Google Analytics 4 (gtag.js) for the admin console. Reports to its own GA
 * property, separate from the participant site.
 *
 * Design notes:
 * - Fully inert when environment.gaMeasurementId is empty, so local/dev/stg
 *   never load gtag.js.
 * - Pages are reported by ROUTE PATTERN, never the real URL. The admin URL
 *   carries org, division and participant ids, invite tokens
 *   (/accept-invite/:token) and query params, so the pattern
 *   (/organizations/:orgId/participants/:userId) is what GA sees. That keeps
 *   ids and tokens out of Google and groups reports by screen.
 * - page_location and page_referrer are also `set` globally so GA's automatic
 *   events (scroll, user_engagement, history page views) carry patterns too,
 *   never window.location. The first referrer is cut to its origin.
 * - Turn OFF "Page changes based on browser history events" in the stream's
 *   enhanced measurement. Those duplicate our page_view and fill the referrer
 *   from the real previous URL.
 * - page_title is the static route title ("Sign in", "Participants"), read
 *   from the route because NavigationEnd fires before the document title
 *   updates. Route titles hold no names.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly titles = inject(TitleStrategy);
  private readonly document = inject(DOCUMENT);
  private readonly measurementId = environment.gaMeasurementId;
  private initialized = false;
  private referrer = originOf(this.document.referrer);

  /** True only when a measurement id is configured for this environment. */
  get enabled(): boolean {
    return !!this.measurementId;
  }

  /** Inject gtag.js and start tracking route changes. Safe to call once at app start. */
  init(): void {
    if (this.initialized || !this.enabled) return;
    const win = this.document.defaultView as GtagWindow | null;
    if (!win) return;
    this.initialized = true;

    // The shim MUST push the `arguments` object, not a rest-param array.
    // gtag.js silently ignores plain arrays, so no hits would ever be sent.
    win.dataLayer = win.dataLayer || [];
    win.gtag = function (..._args: unknown[]) {
      // eslint-disable-next-line prefer-rest-params
      win.dataLayer!.push(arguments);
    };

    win.gtag('js', new Date());
    win.gtag('config', this.measurementId, {
      send_page_view: false,
      page_location: this.location('/'),
      page_referrer: this.referrer,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    this.document.head.appendChild(script);

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const snapshot = this.router.routerState.snapshot;
        this.pageView(routePattern(snapshot.root), this.titles.buildTitle(snapshot) ?? 'VentureOff Admin');
      });
  }

  /** Send a page_view for a route pattern. */
  private pageView(pattern: string, title: string): void {
    const gtag = (this.document.defaultView as GtagWindow | null)?.gtag;
    if (!gtag) return;
    const location = this.location(pattern);
    const page = { page_location: location, page_referrer: this.referrer, page_path: pattern, page_title: title };
    gtag('set', page);
    gtag('event', 'page_view', page);
    this.referrer = location;
  }

  private location(pattern: string): string {
    return `${this.document.defaultView?.location.origin ?? ''}${pattern}`;
  }
}

/** Origin of a URL ("" when empty or unparseable), so no path or query leaks. */
function originOf(url: string): string {
  try {
    return url ? new URL(url).origin : '';
  } catch {
    return '';
  }
}

/** Join the matched route config paths, e.g. /organizations/:orgId/participants. */
export function routePattern(root: ActivatedRouteSnapshot): string {
  const parts: string[] = [];
  for (let node: ActivatedRouteSnapshot | null = root; node; node = node.firstChild) {
    const path = node.routeConfig?.path;
    if (path) parts.push(path);
  }
  return `/${parts.join('/')}`;
}
